// Establishment Context - State management for Establishment module
import React, { createContext, useContext, useState, useCallback } from 'react';
import { establishmentService, establishmentDetailService, establishmentSplitCodeService } from '../services/establishmentService';

const EstablishmentContext = createContext();

export const useEstablishment = () => {
  const context = useContext(EstablishmentContext);
  if (!context) {
    throw new Error('useEstablishment must be used within EstablishmentProvider');
  }
  return context;
};

export const EstablishmentProvider = ({ children }) => {
  const [establishments, setEstablishments] = useState([]);
  const [currentEstablishment, setCurrentEstablishment] = useState(null);
  const [workerAssignments, setWorkerAssignments] = useState([]);
  const [splitCodes, setSplitCodes] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all establishments
  const fetchEstablishments = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const data = await establishmentService.getEstablishments(filters);
      setEstablishments(data || []);
      return data || [];
    } catch (err) {
      // Don't throw, just set error state and return empty array
      const errorMessage = err.message || 'Failed to fetch establishments';
      setError(errorMessage);
      console.warn('Establishment fetch error:', errorMessage);
      setEstablishments([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch single establishment
  const fetchEstablishmentById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const data = await establishmentService.getEstablishmentById(id);
      setCurrentEstablishment(data);
      return data;
    } catch (err) {
      // Don't throw, just set error state
      const errorMessage = err.message || 'Failed to fetch establishment';
      setError(errorMessage);
      console.warn('Establishment fetch error:', errorMessage);
      setCurrentEstablishment(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create establishment
  const createEstablishment = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await establishmentService.createEstablishment(data);
      // Only refresh if API is available (result has id and no warning message)
      if (result && result.id && !result.message?.includes('not available')) {
        await fetchEstablishments(); // Refresh list
      }
      return result;
    } catch (err) {
      // Don't throw, just set error state
      const errorMessage = err.message || 'Failed to create establishment';
      setError(errorMessage);
      console.warn('Establishment create error:', errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchEstablishments]);

  // Update establishment
  const updateEstablishment = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await establishmentService.updateEstablishment(id, data);
      if (result) {
        setCurrentEstablishment(result);
        // Only refresh if API is available
        if (!result.message?.includes('not available')) {
          await fetchEstablishments(); // Refresh list
        }
      }
      return result;
    } catch (err) {
      // Don't throw, just set error state
      const errorMessage = err.message || 'Failed to update establishment';
      setError(errorMessage);
      console.warn('Establishment update error:', errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchEstablishments]);

  // Delete establishment
  const deleteEstablishment = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const result = await establishmentService.deleteEstablishment(id);
      // Only refresh if API is available
      if (!result?.message?.includes('not available')) {
        await fetchEstablishments(); // Refresh list
      } else {
        // Remove from local state if API not available
        setEstablishments(prev => prev.filter(e => e.id !== id));
      }
    } catch (err) {
      // Don't throw, just set error state
      const errorMessage = err.message || 'Failed to delete establishment';
      setError(errorMessage);
      console.warn('Establishment delete error:', errorMessage);
    } finally {
      setLoading(false);
    }
  }, [fetchEstablishments]);

  // Fetch establishment history
  const fetchHistory = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const data = await establishmentService.getEstablishmentHistory(id);
      setHistory(data || []);
      return data || [];
    } catch (err) {
      // Don't throw, just set error state
      const errorMessage = err.message || 'Failed to fetch history';
      setError(errorMessage);
      console.warn('History fetch error:', errorMessage);
      setHistory([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Worker assignment operations
  const fetchWorkerAssignments = useCallback(async (establishmentId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await establishmentDetailService.getWorkerAssignments(establishmentId);
      setWorkerAssignments(data || []);
      return data || [];
    } catch (err) {
      // Don't throw, just set error state
      const errorMessage = err.message || 'Failed to fetch worker assignments';
      setError(errorMessage);
      console.warn('Worker assignments fetch error:', errorMessage);
      setWorkerAssignments([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const createWorkerAssignment = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await establishmentDetailService.createWorkerAssignment(data);
      if (currentEstablishment && result && !result.message?.includes('not available')) {
        await fetchWorkerAssignments(currentEstablishment.id);
      } else if (result && result.message?.includes('not available')) {
        // Add to local state if API not available
        setWorkerAssignments(prev => [...prev, result]);
      }
      return result;
    } catch (err) {
      // Don't throw, just set error state
      const errorMessage = err.message || 'Failed to create worker assignment';
      setError(errorMessage);
      console.warn('Worker assignment create error:', errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [currentEstablishment, fetchWorkerAssignments]);

  const updateWorkerAssignment = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await establishmentDetailService.updateWorkerAssignment(id, data);
      if (currentEstablishment && result && !result.message?.includes('not available')) {
        await fetchWorkerAssignments(currentEstablishment.id);
      }
      return result;
    } catch (err) {
      // Don't throw, just set error state
      const errorMessage = err.message || 'Failed to update worker assignment';
      setError(errorMessage);
      console.warn('Worker assignment update error:', errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [currentEstablishment, fetchWorkerAssignments]);

  // Split code operations
  const fetchSplitCodes = useCallback(async (establishmentId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await establishmentSplitCodeService.getSplitCodes(establishmentId);
      setSplitCodes(data || []);
      return data || [];
    } catch (err) {
      // Don't throw, just set error state
      const errorMessage = err.message || 'Failed to fetch split codes';
      setError(errorMessage);
      console.warn('Split codes fetch error:', errorMessage);
      setSplitCodes([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const createSplitCode = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await establishmentSplitCodeService.createSplitCode(data);
      if (currentEstablishment && result && !result.message?.includes('not available')) {
        await fetchSplitCodes(currentEstablishment.id);
      } else if (result && result.message?.includes('not available')) {
        // Add to local state if API not available
        setSplitCodes(prev => [...prev, result]);
      }
      return result;
    } catch (err) {
      // Don't throw, just set error state
      const errorMessage = err.message || 'Failed to create split code';
      setError(errorMessage);
      console.warn('Split code create error:', errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [currentEstablishment, fetchSplitCodes]);

  const updateSplitCode = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await establishmentSplitCodeService.updateSplitCode(id, data);
      if (currentEstablishment && result && !result.message?.includes('not available')) {
        await fetchSplitCodes(currentEstablishment.id);
      }
      return result;
    } catch (err) {
      // Don't throw, just set error state
      const errorMessage = err.message || 'Failed to update split code';
      setError(errorMessage);
      console.warn('Split code update error:', errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [currentEstablishment, fetchSplitCodes]);

  const deleteSplitCode = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const result = await establishmentSplitCodeService.deleteSplitCode(id);
      if (currentEstablishment && result && !result.message?.includes('not available')) {
        await fetchSplitCodes(currentEstablishment.id);
      } else {
        // Remove from local state if API not available
        setSplitCodes(prev => prev.filter(code => code.id !== id));
      }
    } catch (err) {
      // Don't throw, just set error state
      const errorMessage = err.message || 'Failed to delete split code';
      setError(errorMessage);
      console.warn('Split code delete error:', errorMessage);
    } finally {
      setLoading(false);
    }
  }, [currentEstablishment, fetchSplitCodes]);

  const value = {
    // State
    establishments,
    currentEstablishment,
    workerAssignments,
    splitCodes,
    history,
    loading,
    error,
    // Establishment operations
    fetchEstablishments,
    fetchEstablishmentById,
    createEstablishment,
    updateEstablishment,
    deleteEstablishment,
    fetchHistory,
    setCurrentEstablishment,
    // Worker assignment operations
    fetchWorkerAssignments,
    createWorkerAssignment,
    updateWorkerAssignment,
    // Split code operations
    fetchSplitCodes,
    createSplitCode,
    updateSplitCode,
    deleteSplitCode,
  };

  return (
    <EstablishmentContext.Provider value={value}>
      {children}
    </EstablishmentContext.Provider>
  );
};

export default EstablishmentContext;

